// Shared UI primitives for the Diamond Verification module.
// Extracted from /app/frontend/src/pages/DiamondVerification.jsx.

import React from 'react';
import { COLORS, CLARITIES, SHAPES, SIZES_BY_SHAPE, SOURCE_META } from './constants';

// ── Chip multi-select helper ─────────────────────────────────────────────────
export const toggle = (arr, val) =>
  arr.includes(val) ? arr.filter(v => v !== val) : [...arr, val];

// ── Source badge ─────────────────────────────────────────────────────────────
export const SourceBadge = ({ source, size = 'sm' }) => {
  const meta = SOURCE_META[source] || SOURCE_META.manual;
  const cls = size === 'lg' ? 'text-xs px-2.5 py-1' : 'text-[10px] px-2 py-0.5';
  return (
    <span
      data-testid={`source-badge-${source || 'manual'}`}
      className={`inline-flex items-center rounded-full font-bold border ${meta.tone} ${cls}`}
    >
      {meta.label}
    </span>
  );
};

// ── Color chip row ───────────────────────────────────────────────────────────
// `compact` packs all 23 colours into a tight 12-column grid (fits in 2 rows on mobile).
// `hideLabel` suppresses the field title (used when the wrapper already provides one).
// `disabledBeyond` (P4) — supply the *worst-allowed* grade letter (the worst
//   actual grade picked in Step 2). Any chip alphabetically AFTER that letter
//   is disabled (worse than actual; cannot be proposed for TDS or any lab).
export const ColorPicker = ({ selected, onChange, label, compact, hideLabel, disabledBeyond }) => {
  const worstIdx = disabledBeyond ? COLORS.indexOf(disabledBeyond) : -1;
  const isDisabled = (c) => worstIdx >= 0 && COLORS.indexOf(c) > worstIdx;
  return (
  <div className={compact ? 'mb-3' : 'mb-4'}>
    {!hideLabel && (
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{label || 'Colour'}</p>
    )}
    {compact ? (
      <div className="grid grid-cols-12 gap-1">
        {COLORS.map(c => {
          const disabled = isDisabled(c);
          return (
          <button
            key={c}
            data-testid={`color-chip-${c}`}
            type="button"
            disabled={disabled}
            onClick={() => !disabled && onChange(toggle(selected, c))}
            className={`aspect-square w-full rounded-full text-[11px] font-bold border-2 transition-all flex items-center justify-center ${
              disabled
                ? 'bg-gray-100 border-gray-200 text-gray-300 cursor-not-allowed line-through'
                : selected.includes(c)
                  ? 'bg-emerald-600 border-emerald-600 text-white'
                  : 'bg-white border-gray-300 text-gray-600 active:bg-gray-50'
            }`}
          >{c}</button>
          );
        })}
      </div>
    ) : (
      <div className="flex flex-wrap gap-1.5">
        {COLORS.map(c => {
          const disabled = isDisabled(c);
          return (
          <button
            key={c}
            data-testid={`color-chip-${c}`}
            type="button"
            disabled={disabled}
            onClick={() => !disabled && onChange(toggle(selected, c))}
            className={`w-9 h-9 rounded-full text-sm font-bold border-2 transition-all ${
              disabled
                ? 'bg-gray-100 border-gray-200 text-gray-300 cursor-not-allowed line-through'
                : selected.includes(c)
                  ? 'bg-emerald-600 border-emerald-600 text-white scale-110'
                  : 'bg-white border-gray-300 text-gray-600'
            }`}
          >{c}</button>
          );
        })}
      </div>
    )}
    {disabledBeyond && (
      <p className="text-[10px] text-amber-700 mt-1.5" data-testid="color-clamp-hint">
        Worse-than-<span className="font-bold">{disabledBeyond}</span> grades disabled (worst actual in Step 2).
      </p>
    )}
  </div>
  );
};

// ── Clarity chip row ─────────────────────────────────────────────────────────
// `disabledBeyond` (P4) — worst-allowed clarity (one of "VVS"/"VS"/"SI"/"I").
//   Chips after that index in CLARITIES are disabled.
export const ClarityPicker = ({ selected, onChange, label, compact, hideLabel, disabledBeyond }) => {
  const worstIdx = disabledBeyond ? CLARITIES.indexOf(disabledBeyond) : -1;
  const isDisabled = (c) => worstIdx >= 0 && CLARITIES.indexOf(c) > worstIdx;
  return (
  <div className={compact ? 'mb-3' : 'mb-4'}>
    {!hideLabel && (
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{label || 'Clarity'}</p>
    )}
    <div className={`flex flex-wrap ${compact ? 'gap-1.5' : 'gap-2'}`}>
      {CLARITIES.map(c => {
        const disabled = isDisabled(c);
        return (
        <button
          key={c}
          data-testid={`clarity-chip-${c}`}
          type="button"
          disabled={disabled}
          onClick={() => !disabled && onChange(toggle(selected, c))}
          className={`rounded-full font-semibold border-2 transition-all ${
            compact ? 'px-3 py-1.5 text-xs' : 'px-4 py-2 text-sm'
          } ${
            disabled
              ? 'bg-gray-100 border-gray-200 text-gray-300 cursor-not-allowed line-through'
              : selected.includes(c)
                ? 'bg-emerald-600 border-emerald-600 text-white'
                : 'bg-white border-gray-300 text-gray-700'
          }`}
        >{c}</button>
        );
      })}
    </div>
    {disabledBeyond && (
      <p className="text-[10px] text-amber-700 mt-1.5" data-testid="clarity-clamp-hint">
        Worse-than-<span className="font-bold">{disabledBeyond}</span> grades disabled (worst actual in Step 2).
      </p>
    )}
  </div>
  );
};

// ── Section card ─────────────────────────────────────────────────────────────
export const Section = ({ title, children }) => (
  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-3">
    {title && <p className="text-sm font-bold text-gray-800 mb-3">{title}</p>}
    {children}
  </div>
);

// ── Numeric input ────────────────────────────────────────────────────────────
export const NumInput = ({ label, value, onChange, placeholder, decimal, testid }) => (
  <div className="mb-3">
    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{label}</label>
    <input
      type="number"
      inputMode={decimal ? 'decimal' : 'numeric'}
      step={decimal ? '0.01' : '1'}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testid}
      className="w-full px-4 py-3 border border-gray-200 rounded-xl text-base text-gray-900 bg-gray-50 focus:outline-none focus:border-emerald-500 focus:bg-white"
    />
  </div>
);

// ── Progress dots ────────────────────────────────────────────────────────────
export const StepDots = ({ current, total = 4 }) => (
  <div className="flex items-center justify-center gap-2 py-3">
    {Array.from({ length: total }, (_, i) => (
      <div key={i} className={`rounded-full transition-all ${
        i + 1 === current ? 'w-6 h-2.5 bg-emerald-600' :
        i + 1 < current  ? 'w-2.5 h-2.5 bg-emerald-400' :
                           'w-2.5 h-2.5 bg-gray-200'
      }`} />
    ))}
  </div>
);

// ── Shape chip picker (single-select) ────────────────────────────────────────
export const ShapePicker = ({ selected, onChange, compact, hideLabel }) => (
  <div className={compact ? 'mb-3' : 'mb-4'}>
    {!hideLabel && (
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Shape</p>
    )}
    <div className={`flex ${compact ? 'gap-2' : 'gap-3'}`}>
      {SHAPES.map(s => (
        <button
          key={s}
          type="button"
          onClick={() => onChange(selected === s ? '' : s)}
          className={`flex-1 rounded-full font-bold border-2 transition-all ${
            compact ? 'py-2 text-xs' : 'py-2.5 text-sm'
          } ${
            selected === s
              ? 'bg-emerald-600 border-emerald-600 text-white scale-[1.02]'
              : 'bg-white border-gray-300 text-gray-700'
          }`}
        >{s}</button>
      ))}
    </div>
  </div>
);

// ── Size chip picker (single-select, shape-dependent) ────────────────────────
export const SizePicker = ({ shape, selected, onChange, compact, hideLabel }) => {
  const sizes = SIZES_BY_SHAPE[shape] || [];
  if (!shape) {
    return (
      <div className={compact ? 'mb-3' : 'mb-4'}>
        {!hideLabel && (
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Size</p>
        )}
        <p className="text-xs text-gray-400 italic">Select a Shape first to see size options</p>
      </div>
    );
  }
  return (
    <div className={compact ? 'mb-3' : 'mb-4'}>
      {!hideLabel && (
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Size</p>
      )}
      <div className={`flex flex-wrap ${compact ? 'gap-1.5' : 'gap-2'}`}>
        {sizes.map(s => (
          <button
            key={s}
            data-testid={`size-chip-${s}`}
            type="button"
            onClick={() => onChange(selected === s ? '' : s)}
            className={`rounded-full font-semibold border-2 transition-all ${
              compact ? 'px-2.5 py-1.5 text-xs' : 'px-3 py-2 text-sm'
            } ${
              selected === s
                ? 'bg-emerald-600 border-emerald-600 text-white'
                : 'bg-white border-gray-300 text-gray-700'
            }`}
          >{s}</button>
        ))}
      </div>
    </div>
  );
};
